import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Plus, Edit2, Trash2, Pin, PinOff, Search, Filter,
  AlertCircle, Clock, Users, Calendar, FileText, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NoticeManagement = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    target_audience: 'all',
    expiry_date: '',
    is_pinned: false
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notices/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotices(data.notices || []);
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
      toast.error('Failed to load notices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingNotice 
        ? `${API_URL}/api/notices/${editingNotice.id}`
        : `${API_URL}/api/notices/`;
      
      const response = await fetch(url, {
        method: editingNotice ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingNotice ? 'Notice updated!' : 'Notice created!');
        setShowModal(false);
        resetForm();
        fetchNotices();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save notice');
      }
    } catch (error) {
      toast.error('Failed to save notice');
    }
  };

  const handleDelete = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notices/${noticeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Notice deleted!');
        fetchNotices();
      }
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const handleTogglePin = async (noticeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notices/${noticeId}/toggle-pin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchNotices();
      }
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const openEditModal = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      description: notice.description,
      priority: notice.priority,
      target_audience: notice.target_audience,
      expiry_date: notice.expiry_date || '',
      is_pinned: notice.is_pinned
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingNotice(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      target_audience: 'all',
      expiry_date: '',
      is_pinned: false
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'all': return 'Everyone';
      case 'students': return 'Students Only';
      case 'staff': return 'Staff Only';
      case 'parents': return 'Parents Only';
      default: return audience;
    }
  };

  const filteredNotices = notices.filter(notice => {
    const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          notice.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || notice.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });

  // Sort: pinned first, then by date
  const sortedNotices = [...filteredNotices].sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Megaphone className="h-7 w-7 text-blue-600" />
            Notice Board
          </h1>
          <p className="text-gray-500 mt-1">Create and manage notices for your institution</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Notice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search notices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          <option value="all">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <p className="text-2xl font-bold text-blue-600">{notices.length}</p>
          <p className="text-sm text-gray-500">Total Notices</p>
        </div>
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <p className="text-2xl font-bold text-red-600">{notices.filter(n => n.priority === 'high').length}</p>
          <p className="text-sm text-gray-500">High Priority</p>
        </div>
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <p className="text-2xl font-bold text-amber-600">{notices.filter(n => n.is_pinned).length}</p>
          <p className="text-sm text-gray-500">Pinned</p>
        </div>
        <div className="bg-white p-4 rounded-xl border shadow-sm">
          <p className="text-2xl font-bold text-green-600">{notices.filter(n => n.is_active !== false).length}</p>
          <p className="text-sm text-gray-500">Active</p>
        </div>
      </div>

      {/* Notices List */}
      <div className="space-y-4">
        {sortedNotices.length > 0 ? sortedNotices.map((notice) => (
          <div 
            key={notice.id}
            className={`bg-white rounded-xl border shadow-sm p-5 hover:shadow-md transition-shadow ${
              notice.is_pinned ? 'ring-2 ring-amber-400' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {notice.is_pinned && (
                    <Pin className="h-4 w-4 text-amber-500 fill-amber-500" />
                  )}
                  <h3 className="font-semibold text-lg text-gray-800">{notice.title}</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(notice.priority)}`}>
                    {notice.priority}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-3">{notice.description}</p>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {getAudienceLabel(notice.target_audience)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatDate(notice.created_at)}
                  </span>
                  {notice.expiry_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Expires: {notice.expiry_date}
                    </span>
                  )}
                  <span className="text-gray-400">
                    By: {notice.created_by_name || notice.created_by}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTogglePin(notice.id)}
                  className={`p-2 rounded-lg hover:bg-gray-100 ${notice.is_pinned ? 'text-amber-500' : 'text-gray-400'}`}
                  title={notice.is_pinned ? 'Unpin' : 'Pin'}
                >
                  {notice.is_pinned ? <PinOff className="h-5 w-5" /> : <Pin className="h-5 w-5" />}
                </button>
                <button
                  onClick={() => openEditModal(notice)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-blue-600"
                  title="Edit"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(notice.id)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-red-600"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )) : (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Megaphone className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No notices yet</h3>
            <p className="text-gray-400 mt-1">Create your first notice to get started</p>
            <Button 
              onClick={() => { resetForm(); setShowModal(true); }}
              className="mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Notice
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editingNotice ? 'Edit Notice' : 'Create Notice'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Notice title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Notice description..."
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Target Audience</label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="all">Everyone</option>
                    <option value="students">Students Only</option>
                    <option value="staff">Staff Only</option>
                    <option value="parents">Parents Only</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Expiry Date (Optional)</label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({...formData, is_pinned: e.target.checked})}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="is_pinned" className="text-sm font-medium">Pin this notice</label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Check className="h-4 w-4 mr-2" />
                  {editingNotice ? 'Update Notice' : 'Create Notice'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeManagement;
